import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

interface UseInputTableValidationParams<TRow, TFormRow> {
  rows: TRow[];
  name?: string;
  fieldName?: string;
  getRowId?: (row: TRow) => string | undefined;
  map?: (row: TRow) => TFormRow;
  detail?: (row: TRow) => TFormRow;
  validateAfterSubmitOnly?: boolean;
  deps?: string;
  validationKey?: string;
}

export const useInputTableValidation = <TRow, TFormRow>({
  rows,
  name,
  fieldName,
  getRowId = (row: TRow) => (row as { uuid?: string }).uuid,
  map,
  detail,
  validateAfterSubmitOnly = true,
  deps,
  validationKey
}: UseInputTableValidationParams<TRow, TFormRow>) => {
  const formFieldName = name ?? fieldName ?? '';
  const rowMap = map ?? detail;
  const validateDeps = deps ?? validationKey;
  const {
    setValue,
    trigger,
    formState: { errors, isSubmitted }
  } = useFormContext();

  const formRows = useMemo(() => rows.map(row => rowMap?.(row) ?? (row as unknown as TFormRow)), [rowMap, rows]);
  const previousValidateDepsRef = useRef(validateDeps);
  const validateDepsChanged = previousValidateDepsRef.current !== validateDeps;
  const rowIndexById = useMemo(() => {
    const indexes = new Map<string, number>();

    rows.forEach((row, index) => {
      const rowId = getRowId(row);
      if (rowId) {
        indexes.set(rowId, index);
      }
    });

    return indexes;
  }, [getRowId, rows]);

  useEffect(() => {
    if (!formFieldName) {
      return;
    }

    previousValidateDepsRef.current = validateDeps;
    const shouldValidate = !validateAfterSubmitOnly || isSubmitted || Boolean(validateDepsChanged && validateDeps);

    setValue(formFieldName, formRows, {
      shouldDirty: true,
      shouldValidate
    });

    if (shouldValidate) {
      void trigger(formFieldName);
    }
  }, [
    formFieldName,
    formRows,
    isSubmitted,
    setValue,
    trigger,
    validateAfterSubmitOnly,
    validateDeps,
    validateDepsChanged
  ]);

  const getCellError = useCallback(
    (rowId: string | undefined, cellFieldName: string) => {
      if (!rowId) {
        return '';
      }

      const rowIndex = rowIndexById.get(rowId);
      if (rowIndex === undefined) {
        return '';
      }

      const tableErrors = (errors as Record<string, any>)[formFieldName];
      const message = tableErrors?.[rowIndex]?.[cellFieldName]?.message;
      return typeof message === 'string' ? message : '';
    },
    [errors, formFieldName, rowIndexById]
  );

  return { cellError: getCellError, getCellError };
};
